import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';

import { SORT_TIMEOUT_MS } from '../../constants/duration';
import { MenuActions } from '../../types/MenuActions';
import { SharedGridProps } from '../../types/SharedGridProps';
import { typedMemo } from '../../utils/typedMemo';
import { Grid } from '../Grid/Grid';
import { GridItem } from '../Grid/GridItem';
import { Item } from './Item';
import SortableItem from './SortableItem';

export type ItemType<T> = {
  item: T;
  /** represents how many columns the item will ocupy on the grid */
  width?: number;
  /** represents how many rows the item will ocupy on the grid */
  height?: number;
  /** if this item can be removed, defaults to true */
  isRemovable?: boolean;
};
interface Props<T = unknown> extends SharedGridProps {
  items: ItemType<T>[];
  itemSettings?: {
    moreActions?: (item: T) => MenuActions<string> | undefined;
    padding?: 's' | 'm' | 'l';
    enableBorder?: boolean;
  };
  onSortEnd: (items: T[], activeItem?: T) => void;
  renderTitle: (item: T) => string;
  onRemoveItem?: (removedItem: T, removedItemIndex: number) => void;
  renderContents?: (item: T) => ReactNode;
}

export const SortableGrid = typedMemo(
  <T extends unknown>({
    items,
    itemSettings,
    onRemoveItem,
    onSortEnd,
    columns = 3,
    rows,
    columnSpacing,
    renderContents,
    renderTitle,
    rowSpacing,
  }: Props<T>) => {
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
    const [activeId, setActiveId] = useState<string | null>(null);

    /* START DATA SOURCE */
    const [localItems, setLocalItems] = useState<
      Array<{
        id: string;
        original: T;
        width?: number;
        height?: number;
        isRemovable?: boolean;
      }>
    >([]);

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        setLocalItems(
          items.map((gridItem) => ({
            id: uuid(),
            original: gridItem.item,
            width: gridItem.width,
            height: gridItem.height,
            isRemovable: gridItem.isRemovable ?? true,
          })),
        );
      }, SORT_TIMEOUT_MS);

      return () => clearTimeout(timeoutId);
    }, [items]);
    /* END DATA SOURCE */

    /* MOTION EVENTS */
    const handleDragStart = useCallback((event: DragStartEvent) => {
      setActiveId(String(event.active.id));
    }, []);

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || !active) return;

        if (active.id !== over.id) {
          setLocalItems((localItems) => {
            const oldIndex = localItems.findIndex(
              (item) => item.id === active.id,
            );
            const newIndex = localItems.findIndex(
              (item) => item.id === over.id,
            );

            const updatedLocalItems = arrayMove(localItems, oldIndex, newIndex);
            onSortEnd(
              updatedLocalItems.map((item) => item?.original),
              localItems[oldIndex]?.original,
            );
            return updatedLocalItems;
          });
        }

        setActiveId(null);
      },
      [localItems],
    );

    const handleDragCancel = useCallback(() => {
      setActiveId(null);
    }, []);
    /* END MOTION EVENTS */

    const currentItem = useMemo(
      () => localItems.find((item) => item.id === activeId),
      [activeId, localItems],
    );

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={localItems} strategy={rectSortingStrategy}>
          <Grid
            columns={columns}
            rows={rows}
            columnSpacing={columnSpacing}
            rowSpacing={rowSpacing}
          >
            {localItems.map((item, itemIndex) => (
              <GridItem key={item.id} width={item.width} height={item.height}>
                <SortableItem
                  enableBorder={itemSettings?.enableBorder}
                  id={item.id}
                  title={renderTitle(item.original)}
                  onRemove={
                    item.isRemovable && onRemoveItem
                      ? () => onRemoveItem?.(item.original, itemIndex)
                      : undefined
                  }
                  actions={itemSettings?.moreActions?.(item.original)}
                  padding={itemSettings?.padding}
                >
                  {renderContents?.(item.original)}
                </SortableItem>
              </GridItem>
            ))}
          </Grid>
        </SortableContext>
        <DragOverlay adjustScale style={{ transformOrigin: '0 0 ' }}>
          {activeId && currentItem ? (
            <Item isDragging title={renderTitle(currentItem.original)}>
              {renderContents?.(currentItem.original)}
            </Item>
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  },
);
